import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, FileText, Minus, Paperclip, Plus, RotateCw, Sparkles, Trash2, Upload, X } from 'lucide-react';
import { KiArbeitet } from '@/components/ki/KiArbeitet';
import { bestaetige } from '@/lib/dialoge';
import {
  deleteMonthFile,
  fetchMonthFileBlob,
  fetchMonthFiles,
  fetchSlipReading,
  readSlip,
  setFillMode,
  uploadMonthFile,
  type LohnmonatWeg,
  type MonatsDatei,
} from '../api/payroll.api';

function groesse(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

const ERLAUBT = ['application/pdf', 'image/png', 'image/jpeg'];

interface LeisteProps {
  userId: string;
  jahr: number;
  monat: number;
  weg: LohnmonatWeg;
  /** Weg A: wann übernommen wurde und was dabei offen blieb (D2). */
  uebernahme?: { importedAt: string | null; hinweise: { code: string; dates?: string[] }[] };
  /** Der Lohnmonat ist fertiggestellt: Der Weg steht fest. */
  fertig: boolean;
  /** Tageszeilen (und damit Weg und Zettel) dürfen geändert werden. */
  darfSchreiben: boolean;
  darfZettelLesen: boolean;
  darfZettelPflegen: boolean;
  /** Kennung des Änderungsvorgangs im fertiggestellten Monat. */
  aenderung?: string;
  /** Welcher Zettel gerade neben dem Raster steht (`null` = keiner). */
  offen: string | null;
  onOeffnen: (id: string | null, dateien: MonatsDatei[]) => void;
  onWeg: (weg: LohnmonatWeg) => void;
  /** Ein Zettel kam dazu oder ging weg — im fertiggestellten Monat ändert das den Stand. */
  onGeaendert: () => void;
  /** I: zu welchem Zettel der Vorschlag gerade offen ist, und ihn öffnen — mit den Zetteln, damit das Blatt daneben stehen kann. */
  vorschlag: string | null;
  onVorschlag: (id: string | null, dateien: MonatsDatei[]) => void;
}

/**
 * Weg des Lohnmonats und hinterlegte Abgabezettel (AP-22 Teil 2, H).
 *
 * Zwei Knöpfe wählen den Weg — **Abgabezettel** (B) oder **Händisch** (C); Weg A
 * kommt von selbst aus der Zeiterfassung und wird hier nur angezeigt. Der Zettel
 * lässt sich bei **jedem** Weg hinterlegen: Er ist ein Beleg, kein Schalter.
 */
export function WegUndZettel({
  userId,
  jahr,
  monat,
  weg,
  uebernahme,
  fertig,
  darfSchreiben,
  darfZettelLesen,
  darfZettelPflegen,
  aenderung,
  offen,
  onOeffnen,
  onWeg,
  onGeaendert,
  vorschlag,
  onVorschlag,
}: LeisteProps) {
  const [dateien, setDateien] = useState<MonatsDatei[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [ziel, setZiel] = useState(false);
  const eingabe = useRef<HTMLInputElement>(null);
  // I: Lesen durch die KI — nur, wo die Firma es eingeschaltet hat und die KI eingerichtet ist.
  const [lesenAn, setLesenAn] = useState(false);
  const [liest, setLiest] = useState<string | null>(null);
  useEffect(() => {
    if (!darfZettelLesen) return;
    fetchSlipReading()
      .then((s) => setLesenAn(s.enabled && s.aiAvailable))
      .catch(() => setLesenAn(false));
  }, [darfZettelLesen]);

  const laden = useCallback(async () => {
    if (!darfZettelLesen) return setDateien([]);
    try {
      setDateien(await fetchMonthFiles(userId, jahr, monat));
    } catch {
      setDateien([]);
    }
  }, [userId, jahr, monat, darfZettelLesen]);

  useEffect(() => {
    void laden();
  }, [laden]);

  const waehle = async (neu: 'SLIP' | 'MANUAL') => {
    if (neu === weg) return;
    setError(null);
    try {
      onWeg(await setFillMode(userId, jahr, monat, neu));
    } catch {
      setError('Der Weg konnte nicht geändert werden.');
    }
  };

  const hinterlege = async (liste: FileList | File[] | null) => {
    const neue = [...(liste ?? [])];
    if (neue.length === 0) return;
    setError(null);
    setLaedt(true);
    try {
      let letzte: MonatsDatei | null = null;
      for (const datei of neue) {
        if (!ERLAUBT.includes(datei.type)) {
          setError(`„${datei.name}": Erlaubt sind PDF, JPG und PNG.`);
          continue;
        }
        if (datei.size > 20 * 1024 * 1024) {
          setError(`„${datei.name}" ist größer als 20 MB.`);
          continue;
        }
        letzte = await uploadMonthFile(userId, jahr, monat, datei, aenderung);
      }
      const alle = await fetchMonthFiles(userId, jahr, monat);
      setDateien(alle);
      if (letzte) {
        onOeffnen(letzte.id, alle);
        onGeaendert();
        // Wer einen Zettel hinterlegt, arbeitet vom Zettel — außer der Monat kommt aus der Zeiterfassung.
        if (!fertig && weg === 'MANUAL' && darfSchreiben) void waehle('SLIP');
      }
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
      setError(
        code === 'PAYROLL_MONTH_FINAL' || code === 'PAYROLL_MONTH_CHANGE_REQUIRED'
          ? 'Der Monat ist fertiggestellt — ein Zettel lässt sich hier nur über „Monat ändern" hinterlegen.'
          : 'Der Zettel konnte nicht hinterlegt werden.',
      );
    } finally {
      setLaedt(false);
      if (eingabe.current) eingabe.current.value = '';
    }
  };

  const entferne = async (d: MonatsDatei) => {
    if (!(await bestaetige(`„${d.name}" entfernen?`, { ok: 'Entfernen', gefaehrlich: true }))) return;
    setError(null);
    try {
      await deleteMonthFile(userId, jahr, monat, d.id, aenderung);
      const alle = dateien.filter((x) => x.id !== d.id);
      setDateien(alle);
      if (offen === d.id) onOeffnen(null, alle);
      if (vorschlag === d.id) onVorschlag(null, alle);
      onGeaendert();
    } catch {
      setError('Der Zettel konnte nicht entfernt werden.');
    }
  };

  const lies = async (d: MonatsDatei) => {
    if (
      d.readAt &&
      !(await bestaetige(`„${d.name}" wurde schon gelesen. Noch einmal lesen? Der bisherige Vorschlag wird ersetzt.`, { ok: 'Neu lesen' }))
    )
      return;
    setError(null);
    setLiest(d.id);
    try {
      await readSlip(userId, jahr, monat, d.id);
      const alle = await fetchMonthFiles(userId, jahr, monat);
      setDateien(alle);
      onVorschlag(d.id, alle);
    } catch (err) {
      const antwort = (err as { response?: { status?: number; data?: { code?: string } } })?.response;
      setError(
        antwort?.data?.code === 'PAYROLL_SLIP_READING_OFF'
          ? 'Das Lesen von Abgabezetteln ist nicht eingeschaltet.'
          : antwort?.data?.code === 'PAYROLL_SLIP_AI_UNAVAILABLE'
            ? 'Für diese Firma ist keine KI eingerichtet.'
            : antwort?.status === 429
              ? 'Das KI-Kontingent ist aufgebraucht — der Zettel wurde nicht gelesen.'
              : 'Der Zettel konnte nicht gelesen werden.',
      );
    } finally {
      setLiest(null);
    }
  };

  const knopf = (aktiv: boolean) =>
    `px-3 py-1 text-sm transition-colors ${
      aktiv ? 'bg-primary text-primary-foreground' : 'bg-background text-foreground hover:bg-muted'
    } disabled:cursor-default disabled:opacity-60`;
  // Im fertiggestellten Monat braucht es eine erlaubte Änderung (Einstellung des Mandanten).
  const darfHinterlegen = darfZettelPflegen && (!fertig || darfSchreiben);

  return (
    <div
      className={`flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border bg-card px-4 py-2.5 shadow-sm ${
        ziel ? 'border-primary' : 'border-border'
      }`}
      onDragOver={(e) => {
        if (!darfHinterlegen) return;
        e.preventDefault();
        setZiel(true);
      }}
      onDragLeave={() => setZiel(false)}
      onDrop={(e) => {
        if (!darfHinterlegen) return;
        e.preventDefault();
        setZiel(false);
        void hinterlege(e.dataTransfer.files);
      }}
    >
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground">Befüllt</span>
        {weg === 'TIMESHEET' ? (
          <span className="rounded-md bg-muted px-2.5 py-1 text-sm text-foreground">
            aus der Zeiterfassung{uebernahme?.importedAt ? `, übernommen am ${tagMonat(uebernahme.importedAt)}` : ''}
          </span>
        ) : (
          <div className="flex overflow-hidden rounded-md border border-border" role="group" aria-label="Weg des Monats">
            <button
              type="button"
              className={knopf(weg === 'SLIP')}
              aria-pressed={weg === 'SLIP'}
              disabled={fertig || !darfSchreiben}
              onClick={() => void waehle('SLIP')}
            >
              Abgabezettel
            </button>
            <button
              type="button"
              className={`${knopf(weg === 'MANUAL')} border-l border-border`}
              aria-pressed={weg === 'MANUAL'}
              disabled={fertig || !darfSchreiben}
              onClick={() => void waehle('MANUAL')}
            >
              Händisch
            </button>
          </div>
        )}
      </div>

      {darfZettelLesen && (
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          <Paperclip size={14} className="flex-shrink-0 text-muted-foreground" aria-hidden="true" />
          {dateien.length === 0 && (
            <span className="text-sm text-muted-foreground">
              Kein Abgabezettel hinterlegt{darfHinterlegen ? ' — Datei hierher ziehen oder' : '.'}
            </span>
          )}
          {dateien.map((d) => (
            <span
              key={d.id}
              className={`inline-flex max-w-full items-center gap-1 rounded-md border px-2 py-0.5 text-sm ${
                offen === d.id ? 'border-primary bg-primary/10' : 'border-border'
              }`}
            >
              <button
                type="button"
                className="inline-flex min-w-0 items-center gap-1 text-foreground hover:underline"
                title={`${d.name} (${groesse(d.sizeBytes)}) — neben dem Raster ansehen`}
                onClick={() => onOeffnen(offen === d.id ? null : d.id, dateien)}
              >
                <FileText size={13} className="flex-shrink-0" aria-hidden="true" />
                <span className="truncate">{d.name}</span>
              </button>
              {d.readAt && (
                <button
                  type="button"
                  className={`inline-flex items-center gap-0.5 rounded px-1 text-xs hover:underline ${vorschlag === d.id ? 'text-primary' : 'text-muted-foreground'}`}
                  title="Den gelesenen Vorschlag ansehen"
                  onClick={() => onVorschlag(vorschlag === d.id ? null : d.id, dateien)}
                >
                  Vorschlag
                </button>
              )}
              {lesenAn && darfZettelPflegen && (
                <button
                  type="button"
                  className="inline-flex items-center gap-0.5 rounded px-1 text-xs text-primary hover:underline disabled:opacity-60"
                  title="Von der KI lesen lassen — liefert einen Vorschlag, nichts wird gespeichert"
                  disabled={liest !== null}
                  onClick={() => void lies(d)}
                >
                  <Sparkles size={12} aria-hidden="true" />
                  {liest === d.id ? <KiArbeitet text="Die KI liest" /> : d.readAt ? 'Neu lesen' : 'Lesen'}
                </button>
              )}
              {darfHinterlegen && (
                <button
                  type="button"
                  className="text-muted-foreground hover:text-destructive"
                  aria-label={`${d.name} entfernen`}
                  title="Entfernen"
                  onClick={() => void entferne(d)}
                >
                  <Trash2 size={13} />
                </button>
              )}
            </span>
          ))}
          {darfHinterlegen && (
            <>
              <input
                ref={eingabe}
                type="file"
                accept={ERLAUBT.join(',')}
                multiple
                className="hidden"
                onChange={(e) => void hinterlege(e.target.files)}
              />
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-sm text-foreground hover:bg-muted disabled:opacity-60"
                disabled={laedt}
                onClick={() => eingabe.current?.click()}
              >
                <Upload size={14} aria-hidden="true" />
                {laedt ? 'Wird hinterlegt…' : 'Zettel hinterlegen'}
              </button>
            </>
          )}
        </div>
      )}
      {uebernahme?.hinweise.map((h) => (
        <p key={h.code} className="w-full text-xs text-amber-700 dark:text-amber-300">
          {UEBERNAHME_TEXT[h.code] ?? h.code}
          {h.dates?.length ? `: ${h.dates.map((d) => `${d.slice(8, 10)}.${d.slice(5, 7)}.`).join(' · ')}` : ''}
        </p>
      ))}
      {error && <p className="w-full text-xs text-destructive">{error}</p>}
    </div>
  );
}

/** Was bei der Übernahme aus der Zeiterfassung offen blieb. */
const UEBERNAHME_TEXT: Record<string, string> = {
  ABSENCE_TYPE_OPEN: 'Fehlzeit ohne zugeordnete Art — bitte am Tag eine Art wählen',
  EXISTING_ROW: 'Hier stand schon eine Zeile; sie ist geblieben und weicht womöglich vom Monatsbericht ab',
  MONTH_FINAL: 'Der Monatsbericht kam nach dem Fertigstellen und wurde nicht übernommen',
};

function tagMonat(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}

/**
 * Der Abgabezettel **neben** dem Raster: blättern, vergrößern, drehen — damit
 * beim Abtippen nicht zwischen zwei Fenstern gewechselt wird. PDFs zeigt der
 * Browser selbst; Bilder lassen sich zoomen und drehen.
 */
export function ZettelAnsicht({
  userId,
  jahr,
  monat,
  dateien,
  offen,
  onWechsel,
  onSchliessen,
  nebenVorschlag,
}: {
  userId: string;
  jahr: number;
  monat: number;
  dateien: MonatsDatei[];
  offen: string;
  onWechsel: (id: string) => void;
  onSchliessen: () => void;
  /** Das Blatt steht links neben dem Vorschlag der KI statt neben dem Raster: niedriger, nicht mitlaufend. */
  nebenVorschlag?: boolean;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [fehler, setFehler] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [drehung, setDrehung] = useState(0);
  const datei = dateien.find((d) => d.id === offen);
  const index = dateien.findIndex((d) => d.id === offen);

  // Die Bytes kommen über das Backend (mit Anmeldung) — nie über einen offenen Link.
  useEffect(() => {
    let aktuell = true;
    let erzeugt: string | null = null;
    setUrl(null);
    setFehler(false);
    setZoom(1);
    setDrehung(0);
    fetchMonthFileBlob(userId, jahr, monat, offen)
      .then((blob) => {
        if (!aktuell) return;
        erzeugt = URL.createObjectURL(blob);
        setUrl(erzeugt);
      })
      .catch(() => aktuell && setFehler(true));
    return () => {
      aktuell = false;
      if (erzeugt) URL.revokeObjectURL(erzeugt);
    };
  }, [userId, jahr, monat, offen]);

  const werkzeug = 'rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40';
  const istBild = datei?.mimeType.startsWith('image/');

  return (
    <aside
      className={`flex flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm ${
        nebenVorschlag ? 'h-[70vh] xl:sticky xl:top-4' : 'h-[calc(100vh-7rem)] lg:sticky lg:top-4'
      }`}
    >
      <div className="flex items-center gap-1 border-b border-border px-2 py-1.5">
        <button
          type="button"
          className={werkzeug}
          aria-label="Vorheriger Zettel"
          disabled={index <= 0}
          onClick={() => onWechsel(dateien[index - 1].id)}
        >
          <ChevronLeft size={16} />
        </button>
        <span className="min-w-0 flex-1 truncate text-center text-sm text-foreground" title={datei?.name}>
          {datei?.name ?? 'Abgabezettel'}
          {dateien.length > 1 ? ` (${index + 1}/${dateien.length})` : ''}
        </span>
        <button
          type="button"
          className={werkzeug}
          aria-label="Nächster Zettel"
          disabled={index < 0 || index >= dateien.length - 1}
          onClick={() => onWechsel(dateien[index + 1].id)}
        >
          <ChevronRight size={16} />
        </button>
        {istBild && (
          <>
            <button type="button" className={werkzeug} aria-label="Verkleinern" onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}>
              <Minus size={16} />
            </button>
            <button type="button" className={werkzeug} aria-label="Vergrößern" onClick={() => setZoom((z) => Math.min(4, z + 0.25))}>
              <Plus size={16} />
            </button>
            <button type="button" className={werkzeug} aria-label="Drehen" onClick={() => setDrehung((d) => (d + 90) % 360)}>
              <RotateCw size={16} />
            </button>
          </>
        )}
        <button type="button" className={werkzeug} aria-label="Ansicht schließen" onClick={onSchliessen}>
          <X size={16} />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto bg-muted/30">
        {fehler && <p className="p-4 text-sm text-destructive">Der Zettel konnte nicht geladen werden.</p>}
        {!url && !fehler && <p className="p-4 text-sm text-muted-foreground">Laden…</p>}
        {url && istBild && (
          <img
            src={url}
            alt={datei?.name ?? 'Abgabezettel'}
            style={{
              width: `${zoom * 100}%`,
              maxWidth: 'none',
              transform: `rotate(${drehung}deg)`,
              transformOrigin: 'center center',
            }}
          />
        )}
        {url && !istBild && <iframe src={url} title={datei?.name ?? 'Abgabezettel'} className="h-full w-full border-0" />}
      </div>
    </aside>
  );
}
